import { createContext, useState, useCallback, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isAIEnabled } from 'shared/store/selectors/features.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import zeusServiceApi from 'shared/api/zeus.service.api';
import { isStringsSimilar } from 'shared/utils';
import { useLiveRegion } from 'src/hooks/accessibility';
import {
  setAIMessages,
  setAIConversationId,
  clearAIConversation,
} from 'shared/store/actions/ai.actions';
import Logger from 'shared/utils/Logger';

const ChatContext = createContext();

/**
 * Get the chat conversation context
 * @returns {{
 * conversationId: string,
 * loading: boolean,
 * messages: object[],
 * lang: object,
 * isOpen: boolean,
 * isChatEnabled: boolean,
 * onMessageSend: (values: { message: string }) => Promise<void>,
 * editMessage: (userMessageId: string, values: { message: string }) => Promise<void>,
 * regenerate: (answerId: string) => Promise<void>,
 * clearChat: () => void,
 * isMessageLoading: (index: number) => boolean,
 * announceLiveRegion: (message: string) => void
 * }}
 */
export function useChatConversation() {
  return useContext(ChatContext);
}

/**
 *
 * @param {{question: object, answer: object}} chat
 * @param {boolean} error
 */
function findMessageIndex(messages, id) {
  return messages.findIndex(msg => {
    const msgId = msg.id.split('-');
    return msgId[0] === id || msgId[1] === id || msg.id === id;
  });
}

function constructMessages(chat, error = false) {
  // for the history questions
  chat.question.history?.forEach((q, i) => {
    if (!isStringsSimilar(q.content, q.refineTerm)) {
      chat.answer.history[i].showOptimized = true;
      chat.answer.history[i].optimizedQuestion = q.refineTerm.trim();
    }
  });
  // for the main question
  if (!isStringsSimilar(chat.question.content, chat.question.refineTerm)) {
    chat.answer.showOptimized = true;
    chat.answer.optimizedQuestion = chat.question.refineTerm.trim();
  }

  return {
    question: {
      ...chat.question,
      error,
      id: chat.question._id,
    },
    answer: {
      ...chat.answer,
      error,
      id: chat.answer._id,
    },
    id: `${chat.question._id}-${chat.answer._id}`,
  };
}

/**
 * Chat provider
 * @export
 * @param {{
 *  children: React.ReactNode;
 *  analyticsTriggers: {
 *   onMessage: (payload: object) => void;
 *   onErrorMessage: (payload: object) => void;
 *   onEditMessage: (payload: object) => void;
 *   onClear: (payload: object) => void;
 * }}} { children, analyticsTriggers = {} }
 */

export function ChatProvider({ children, analyticsTriggers = {} }) {
  const dispatch = useDispatch();
  const messages = useSelector(state => state.ai.messages);
  const conversationId = useSelector(state => state.ai.conversationId);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const [loading, setLoadingState] = useState(false);
  const [loadedQA, setLoadedQA] = useState({
    index: null,
    loading: false,
  });
  const { announce, liveRegion } = useLiveRegion({ 'aria-live': 'assertive' });

  const clearChat = useCallback(() => {
    analyticsTriggers?.onClear({ conversationId, depth: messagesRef.current.length });
    dispatch(clearAIConversation());
  }, [analyticsTriggers, conversationId, dispatch]);

  const isMessageLoading = index => {
    return loadedQA.index === index && loadedQA.loading;
  };

  const setLoadedQAState = useCallback((id, state) => {
    const index = findMessageIndex(messagesRef.current, id);
    setLoadedQA({
      index,
      loading: state,
    });
  }, []);

  const lang = useSelector(getLang('AI_CHAT'));

  const isOpen = useSelector(state => state.ai.isChatOpen);

  const isChatEnabled = useSelector(isAIEnabled);

  const updatedMessage = useCallback(
    ({ id, message = {}, transformFn = null }) => {
      const currentMessages = messagesRef.current;
      const messageIndex = findMessageIndex(currentMessages, id);
      if (messageIndex === -1) {
        Logger.error('updatedMessage: message not found', { id });
        return;
      }

      const updatedMessages = [...currentMessages];
      updatedMessages[messageIndex] = transformFn
        ? transformFn(updatedMessages[messageIndex])
        : message;
      dispatch(setAIMessages(updatedMessages));
    },
    [dispatch],
  );

  const onMessageSend = useCallback(
    async values => {
      setLoadingState(true);
      announce(lang.LOADING_RESPONSE);
      const randomId = Math.random(); // unique id for the temporary message
      // push the user message to the chat temporarily
      const tempQA = {
        id: `${randomId}-${randomId}`,
        question: {
          id: randomId,
          content: values.message,
        },
        answer: {
          id: randomId,
          content: '',
        },
      };

      const currentMessages = messagesRef.current;

      dispatch(setAIMessages([...currentMessages, tempQA]));
      setLoadedQA({ index: currentMessages.length, loading: true });

      const handleError = () => {
        updatedMessage({
          id: tempQA.id,
          message: {
            id: `${randomId}-${randomId}`,
            question: {
              id: randomId,
              content: values.message,
              error: true,
            },
            answer: {
              id: randomId,
              content: lang.ERROR_MESSAGE,
              error: true,
            },
          },
        });
        announce(`${lang.BOT_NAME}: ${lang.ERROR_MESSAGE}`);

        analyticsTriggers?.onErrorMessage({
          conversationId,
          depth: messagesRef.current.length,
        });
      };

      try {
        const res = await zeusServiceApi.chatMessage({ term: values.message, conversationId });

        if (res.success) {
          const { conversationId, chat } = res.data;
          const qAndA = constructMessages(chat);
          updatedMessage({ id: tempQA.id, message: qAndA });
          dispatch(setAIConversationId(conversationId));
          announce(`${lang.BOT_NAME} answer: ${chat.answer.content}`);
          analyticsTriggers?.onMessage({
            conversationId,
            depth: messagesRef.current.length,
            references: chat?.answer?.references?.length,
            knowledgeItemId: chat?.answer?.references?.[0]?.knowledgeItemId,
            resources: chat?.answer?.resources?.length,
            state: chat?.answer?.state,
            historyState: chat?.answer?.historyState,
          });
        } else {
          handleError();
        }
      } catch {
        handleError();
      } finally {
        setLoadedQA({ index: messagesRef.current.length, loading: false });
        setLoadingState(false);
      }
    },
    [
      conversationId,
      lang.ERROR_MESSAGE,
      lang.BOT_NAME,
      lang.LOADING_RESPONSE,
      analyticsTriggers,
      announce,
      dispatch,
      updatedMessage,
    ],
  );

  const onHistoryResponse = useCallback(
    async (res, oldMessage, messageId) => {
      if (res.success) {
        const { chat } = res.data;
        const qAndA = constructMessages(chat, false);
        updatedMessage({ id: messageId, message: qAndA });
        announce(`${lang.BOT_NAME}: ${chat.answer.content}`);
      } else {
        const randomId = Math.random();
        updatedMessage({
          id: messageId,
          transformFn: prevQA => ({
            question: {
              id: randomId,
              content: oldMessage ? oldMessage : prevQA.question.content,
              error: true,
              history: [...(prevQA.question.history || []), prevQA.question],
            },
            answer: {
              id: randomId,
              content: lang.ERROR_MESSAGE,
              error: true,
              history: [...(prevQA.answer.history || []), prevQA.answer],
            },
            id: `${randomId}-${randomId}`,
          }),
        });
        announce(`${lang.BOT_NAME}: ${lang.ERROR_MESSAGE}`);
      }
    },
    [lang.ERROR_MESSAGE, lang.BOT_NAME, announce, updatedMessage],
  );

  const editMessage = useCallback(
    async (questionId, values) => {
      setLoadingState(true);
      announce(lang.UPDATING_MESSAGE);
      setLoadedQAState(questionId, true);

      // remove all the following messages on edit
      const currentMessages = messagesRef.current;
      const messageIndex = findMessageIndex(currentMessages, questionId);
      if (messageIndex !== -1) {
        dispatch(setAIMessages(currentMessages.slice(0, messageIndex + 1)));
      }

      try {
        const res = await zeusServiceApi.editMessage(conversationId, questionId, {
          term: values.message,
        });

        onHistoryResponse(res, values.message, questionId);

        let payload;

        if (res.success) {
          const { chat } = res.data;
          payload = {
            references: chat?.answer?.references?.length,
            knowledgeItemId: chat?.answer?.references?.[0]?.knowledgeItemId,
            resources: chat?.answer?.resources?.length,
            state: chat?.answer?.state,
            historyState: chat?.answer?.historyState,
          };
        } else {
          payload = {
            error: true,
          };
        }

        analyticsTriggers?.onEditMessage({
          conversationId,
          depth: messagesRef.current.length,
          ...payload,
        });
      } catch {
        onHistoryResponse({ success: false }, values.message, questionId);

        analyticsTriggers?.onEditMessage({
          conversationId,
          depth: messagesRef.current.length,
          error: true,
        });
      } finally {
        setLoadedQAState(questionId, false);
        setLoadingState(false);
      }
    },
    [
      conversationId,
      onHistoryResponse,
      setLoadedQAState,
      analyticsTriggers,
      announce,
      lang.UPDATING_MESSAGE,
      dispatch,
    ],
  );

  const regenerate = useCallback(
    async answerId => {
      setLoadingState(true);
      setLoadedQAState(answerId, true);

      try {
        const res = await zeusServiceApi.regenerate(conversationId, answerId);
        onHistoryResponse(res, '', answerId);
      } catch {
        onHistoryResponse({ success: false }, '', answerId);
      } finally {
        setLoadedQAState(answerId, false);
        setLoadingState(false);
      }
    },
    [conversationId, onHistoryResponse, setLoadedQAState],
  );

  const value = {
    conversationId,
    loading,
    messages,
    lang,
    isOpen,
    isChatEnabled,
    onMessageSend,
    editMessage,
    regenerate,
    clearChat,
    isMessageLoading,
    announceLiveRegion: announce,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
      {liveRegion}
    </ChatContext.Provider>
  );
}
